import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, CaretDown } from '@/core/ui/icons';
import { useState, type ReactNode } from 'react';
import { Controller, useForm, useWatch, type FieldPath } from 'react-hook-form';
import { errorMessage, isApiError } from '@/core/http';
import {
  Badge,
  Button,
  Card,
  cn,
  Dialog,
  Field,
  Input,
  Page,
  Select,
  Textarea,
  type BadgeTone,
} from '@/core/ui';
import { useDeleteEvent, useEventStatus, useSaveEvent } from '../application/queries';
import {
  ACCENTS,
  collapseSpaces,
  decimalInput,
  digitsOnly,
  emptyForm,
  eventFormSchema,
  FORMATS,
  formatWhen,
  LIMITS,
  normalizeUrl,
  publishBlockers,
  seatsLabel,
  STATUS_LABELS,
  toBody,
  toForm,
  type AcademyEvent,
  type EventForm,
} from '../domain/event';
import { RegistrationsTable } from './RegistrationsTable';

const TONES: Record<string, BadgeTone> = { draft: 'warning', published: 'success', cancelled: 'danger' };

/** Campo de imagem injetado pelo backoffice (contexto media). */
export type ImageSlot = (props: {
  url: string | null;
  onChange: (value: { key: string; url: string } | null) => void;
}) => ReactNode;

interface Props {
  event?: AcademyEvent;
  canEdit: boolean;
  imageSlot: ImageSlot;
  onBack: () => void;
  onSaved: (event: AcademyEvent) => void;
  onDeleted: () => void;
}

/** Criação e edição de evento, com publicação e inscritos. */
export function EventEditor({ event, canEdit, imageSlot, onBack, onSaved, onDeleted }: Props) {
  const save = useSaveEvent();
  const form = useForm<EventForm>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: event ? toForm(event) : emptyForm(),
    mode: 'onTouched',
  });
  const { errors, isDirty } = form.formState;
  const format = useWatch({ control: form.control, name: 'format' });
  const imageUrl = useWatch({ control: form.control, name: 'imageUrl' });

  /**
   * Registra o campo com formatação: `change` reescreve enquanto se digita
   * (vagas, coordenadas); `blur` arruma ao sair (espaços, link).
   */
  const field = (
    name: FieldPath<EventForm>,
    fmt?: { on: 'change' | 'blur'; apply: (v: string) => string },
  ) => {
    const reg = form.register(name);
    if (!fmt) return reg;
    const set = (v: string) =>
      form.setValue(name, fmt.apply(v) as never, { shouldDirty: true, shouldValidate: true });
    return {
      ...reg,
      onChange:
        fmt.on === 'change'
          ? async (e: React.ChangeEvent<HTMLInputElement>) => set(e.target.value)
          : reg.onChange,
      onBlur: async (e: React.FocusEvent<HTMLInputElement>) => {
        if (fmt.on === 'blur') set(e.target.value);
        await reg.onBlur(e);
      },
    };
  };
  const tidy = { on: 'blur', apply: collapseSpaces } as const;

  const submit = form.handleSubmit(async (values) => {
    try {
      const saved = await save.mutateAsync({ id: event?.id, body: toBody(values) });
      form.reset(toForm(saved));
      onSaved(saved);
    } catch (e) {
      if (isApiError(e) && e.is('duplicate_slug'))
        form.setError('title', { message: 'Já existe um evento com este título' });
    }
  });

  return (
    <Page>
      <button
        type="button"
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-1.5 text-sm text-fg-subtle hover:text-fg"
      >
        <ArrowLeft aria-hidden className="size-4" />
        Eventos
      </button>

      <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-fg sm:text-3xl">{event ? event.title : 'Novo evento'}</h1>
            {event && <Badge tone={TONES[event.status] ?? 'neutral'}>{STATUS_LABELS[event.status]}</Badge>}
          </div>
          {event && (
            <p className="mt-1 text-base text-fg-subtle">
              {formatWhen(event.starts_at)} · {seatsLabel(event)}
            </p>
          )}
        </div>
        {canEdit && (
          <Button size="lg" onClick={submit} loading={save.isPending} disabled={!!event && !isDirty}>
            {event ? 'Salvar alterações' : 'Criar evento'}
          </Button>
        )}
      </header>

      {save.isError && !(isApiError(save.error) && save.error.is('duplicate_slug')) && (
        <p
          role="alert"
          className="mb-6 rounded-xl border border-line-strong bg-surface px-4 py-3 text-sm text-fg"
        >
          {errorMessage(save.error)}
        </p>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <form onSubmit={submit} noValidate aria-label="Dados do evento" className="min-w-0">
          <fieldset disabled={!canEdit} className="space-y-6">
            <Section title="Informações">
              <Field label="Título" error={errors.title?.message}>
                {(p) => <Input {...p} maxLength={LIMITS.title.max} {...field('title', tidy)} />}
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Categoria" error={errors.category?.message}>
                  {(p) => (
                    <Input
                      {...p}
                      maxLength={LIMITS.category}
                      placeholder="Congresso"
                      {...field('category', tidy)}
                    />
                  )}
                </Field>
                <Field label="Organizador" error={errors.organizer?.message}>
                  {(p) => (
                    <Input
                      {...p}
                      maxLength={LIMITS.organizer}
                      placeholder="Central Nutrition"
                      {...field('organizer', tidy)}
                    />
                  )}
                </Field>
              </div>
              <Field
                label="Descrição"
                error={errors.description?.message}
                hint="Parágrafos separados por uma linha em branco."
              >
                {(p) => (
                  <Textarea
                    {...p}
                    rows={6}
                    maxLength={LIMITS.description}
                    {...form.register('description')}
                  />
                )}
              </Field>
            </Section>

            <Section title="Data e local">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Início" error={errors.startsAt?.message}>
                  {(p) => <Input {...p} type="datetime-local" {...form.register('startsAt')} />}
                </Field>
                <Field label="Término (opcional)" error={errors.endsAt?.message}>
                  {(p) => <Input {...p} type="datetime-local" {...form.register('endsAt')} />}
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Formato">
                  {(p) => (
                    <Select {...p} {...form.register('format')}>
                      {FORMATS.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="Vagas" error={errors.capacity?.message} hint="Em branco: sem limite.">
                  {(p) => (
                    <Input
                      {...p}
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="Ilimitado"
                      {...field('capacity', { on: 'change', apply: digitsOnly })}
                    />
                  )}
                </Field>
              </div>

              {format !== 'online' && (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Local" error={errors.venue?.message}>
                      {(p) => (
                        <Input
                          {...p}
                          maxLength={LIMITS.venue}
                          placeholder="Shopping Vale do Aço"
                          {...field('venue', tidy)}
                        />
                      )}
                    </Field>
                    <Field label="Cidade" error={errors.city?.message}>
                      {(p) => (
                        <Input
                          {...p}
                          maxLength={LIMITS.city}
                          placeholder="Ipatinga – MG"
                          {...field('city', tidy)}
                        />
                      )}
                    </Field>
                  </div>
                  <Field label="Endereço" error={errors.address?.message}>
                    {(p) => <Input {...p} maxLength={LIMITS.address} {...field('address', tidy)} />}
                  </Field>
                  <Disclosure title="Posição no mapa" hint="opcional — mostra o mapa na página do evento">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Latitude" error={errors.latitude?.message}>
                        {(p) => (
                          <Input
                            {...p}
                            inputMode="decimal"
                            placeholder="-19,4706"
                            {...field('latitude', { on: 'change', apply: decimalInput })}
                          />
                        )}
                      </Field>
                      <Field label="Longitude" error={errors.longitude?.message}>
                        {(p) => (
                          <Input
                            {...p}
                            inputMode="decimal"
                            placeholder="-42,5378"
                            {...field('longitude', { on: 'change', apply: decimalInput })}
                          />
                        )}
                      </Field>
                    </div>
                  </Disclosure>
                </>
              )}
              {format !== 'presencial' && (
                <Field label="Link da transmissão (opcional)" error={errors.onlineUrl?.message}>
                  {(p) => (
                    <Input
                      {...p}
                      type="url"
                      inputMode="url"
                      maxLength={LIMITS.onlineUrl}
                      placeholder="https://"
                      {...field('onlineUrl', { on: 'blur', apply: normalizeUrl })}
                    />
                  )}
                </Field>
              )}
            </Section>

            <Section
              title="Banner"
              description="Imagem do evento no portal. Sem ela, o portal gera uma arte."
            >
              <Controller
                control={form.control}
                name="imageKey"
                render={({ field: f }) => (
                  <>
                    {imageSlot({
                      url: imageUrl,
                      onChange: (v) => {
                        f.onChange(v?.key ?? null);
                        form.setValue('imageUrl', v?.url ?? null, { shouldDirty: true });
                      },
                    })}
                  </>
                )}
              />
              <label className="flex items-center gap-2.5 text-sm text-fg-muted">
                <input type="checkbox" className="size-4 accent-teal-500" {...form.register('featured')} />
                Destacar no topo da página de eventos
              </label>
              <Disclosure title="Textos da arte gerada" hint="usados quando não há banner">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Linha pequena" error={errors.kicker?.message}>
                    {(p) => (
                      <Input
                        {...p}
                        maxLength={LIMITS.kicker}
                        placeholder="III Congresso de"
                        {...field('kicker', tidy)}
                      />
                    )}
                  </Field>
                  <Field label="Série" error={errors.series?.message}>
                    {(p) => (
                      <Input
                        {...p}
                        maxLength={LIMITS.series}
                        placeholder="Saúde Integrativa"
                        {...field('series', tidy)}
                      />
                    )}
                  </Field>
                  <Field label="Chamada" error={errors.headline?.message}>
                    {(p) => (
                      <Input
                        {...p}
                        maxLength={LIMITS.headline}
                        placeholder="Saúde integrativa:"
                        {...field('headline', tidy)}
                      />
                    )}
                  </Field>
                  <Field label="Complemento" error={errors.tagline?.message}>
                    {(p) => (
                      <Input
                        {...p}
                        maxLength={LIMITS.tagline}
                        placeholder="mais equilíbrio, mais vida"
                        {...field('tagline', tidy)}
                      />
                    )}
                  </Field>
                  <Field label="Cor de destaque">
                    {(p) => (
                      <Select {...p} {...form.register('accent')}>
                        {ACCENTS.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.label}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </div>
              </Disclosure>
            </Section>

          </fieldset>
        </form>

        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          {event && canEdit && <Lifecycle event={event} onDeleted={onDeleted} dirty={isDirty} />}
          {event && <RegistrationsTable eventId={event.id} />}
          {!event && (
            <Card className="p-5 text-sm text-fg-muted">
              O evento nasce como <strong className="text-fg">rascunho</strong>. Depois de criado, você
              publica e acompanha os inscritos por aqui.
            </Card>
          )}
        </aside>
      </div>
    </Page>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card className="space-y-5 p-6">
      <div>
        <h2 className="text-lg font-semibold text-fg">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-fg-subtle">{description}</p>}
      </div>
      {children}
    </Card>
  );
}

/** Bloco recolhível para o que é opcional — some da frente, não da tela. */
function Disclosure({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-line">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm"
      >
        <span className="font-medium text-fg">{title}</span>
        {hint && <span className="text-fg-subtle">· {hint}</span>}
        <CaretDown
          aria-hidden
          className={cn('ml-auto size-4 text-gray-400 transition-transform', open && 'rotate-180')}
        />
      </button>
      {/* Montado mesmo fechado: erro de validação dentro dele precisa existir. */}
      <div hidden={!open} className="border-t border-line px-4 py-4">
        {children}
      </div>
    </div>
  );
}

function Lifecycle({
  event,
  onDeleted,
  dirty,
}: {
  event: AcademyEvent;
  onDeleted: () => void;
  dirty: boolean;
}) {
  const status = useEventStatus();
  const del = useDeleteEvent();
  const [confirm, setConfirm] = useState<'cancel' | 'delete' | null>(null);
  const run = (action: 'publish' | 'unpublish' | 'cancel') => status.mutate({ id: event.id, action });
  const blockers = publishBlockers(event);

  return (
    <Card className="space-y-4 p-5">
      <h2 className="text-base font-semibold">Publicação</h2>
      {event.status === 'draft' && (
        <>
          <p className="text-sm text-fg-muted">Rascunho: só aparece aqui no backoffice.</p>
          {blockers.length > 0 && (
            <ul className="space-y-1 rounded-lg bg-surface-2 px-3 py-2 text-sm text-fg-muted">
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
          <Button
            className="w-full"
            loading={status.isPending}
            disabled={dirty || blockers.length > 0}
            onClick={() => run('publish')}
          >
            Publicar no portal
          </Button>
          {dirty && <p className="text-xs text-fg-subtle">Salve as alterações antes de publicar.</p>}
        </>
      )}
      {event.status === 'published' && (
        <>
          <p className="text-sm text-fg-muted">Publicado: visível no portal e aceitando inscrições.</p>
          <Button
            variant="secondary"
            className="w-full"
            loading={status.isPending}
            onClick={() => run('unpublish')}
          >
            Voltar para rascunho
          </Button>
          <Button variant="danger" className="w-full" onClick={() => setConfirm('cancel')}>
            Cancelar evento
          </Button>
        </>
      )}
      {event.status === 'cancelled' && (
        <p className="text-sm text-fg-muted">Cancelado: não aceita inscrições.</p>
      )}
      {event.registrations === 0 && (
        <button
          type="button"
          onClick={() => setConfirm('delete')}
          className="w-full text-center text-sm text-fg-subtle hover:text-fg hover:underline"
        >
          Apagar evento
        </button>
      )}
      {(status.error ?? del.error) && (
        <p role="alert" className="text-sm text-fg">
          {errorMessage(status.error ?? del.error)}
        </p>
      )}

      <Dialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm === 'cancel' ? 'Cancelar o evento?' : 'Apagar o evento?'}
        description={event.title}
      >
        <p className="text-sm text-fg-muted">
          {confirm === 'cancel'
            ? 'O evento continua no portal como cancelado e para de aceitar inscrições. Não dá para publicar de novo.'
            : 'O evento some de vez. Não dá para desfazer.'}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirm(null)}>
            Voltar
          </Button>
          <Button
            variant="danger"
            loading={status.isPending || del.isPending}
            onClick={() =>
              confirm === 'cancel'
                ? status.mutate({ id: event.id, action: 'cancel' }, { onSuccess: () => setConfirm(null) })
                : del.mutate(event.id, { onSuccess: onDeleted })
            }
          >
            {confirm === 'cancel' ? 'Cancelar evento' : 'Apagar'}
          </Button>
        </div>
      </Dialog>
    </Card>
  );
}
